import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect } from "react";
import { useAuth } from "@/lib/auth-context";

import { ArrowRight } from "lucide-react";
import logoHorizontal from "@/assets/wc-logo-horizontal-white.png.asset.json";
import heroImage from "@/assets/hero-workspace.jpg";
import { Button } from "@/components/ui/button";
import { DemoEntry } from "@/components/demo-entry";


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "The Wealthy Creatives Boardroom — Lead Like A Creative CEO" },
      {
        name: "description",
        content:
          "A CEO dashboard built for creative entrepreneurs. See your revenue, work your pipeline, and lead your studio from one calm command room — so your talent finally has an operator worthy of it.",
      },
      { property: "og:title", content: "The Wealthy Creatives Boardroom" },
      {
        property: "og:description",
        content:
          "A CEO dashboard for creative entrepreneurs. See your revenue, work your pipeline, and lead your studio from one calm command room.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/" }],
  }),
  component: LandingPage,
});

const GOLD = "#C9A84C";
const TEAL = "#5FA8A8";

function Nav() {
  return (
    <nav className="absolute top-0 inset-x-0 z-30 px-4 sm:px-6 lg:px-12 py-6 flex flex-wrap gap-4 items-center justify-between">
      <img src={logoHorizontal.url} alt="The Wealthy Creatives Boardroom" className="h-7 md:h-8 w-auto" />
      <div className="flex items-center gap-3">
        <DemoEntry className="border-border bg-background/60 text-foreground" />
        <Link to="/auth" className="hidden sm:inline-block text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors">
          Sign In
        </Link>
        <Link
          to="/auth"
          className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/15 text-white text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors"
        >
          Enter
        </Link>
      </div>
    </nav>
  );
}

function DashboardMockup() {
  return (
    <div className="relative w-full max-w-xl mx-auto" style={{ transform: "perspective(1800px) rotateY(-14deg) rotateX(4deg)" }}>
      <div
        className="absolute -inset-10 rounded-[2rem] blur-3xl opacity-40 -z-10"
        style={{ background: `radial-gradient(circle at 30% 30%, ${GOLD}, transparent 60%), radial-gradient(circle at 70% 70%, ${TEAL}, transparent 60%)` }}
      />
      <div className="relative rounded-2xl border border-white/10 bg-[#141414]/95 backdrop-blur-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-green-500/70" />
          </div>
          <span className="text-[10px] tracking-[0.25em] text-white/40 uppercase">Boardroom · Mon</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-white/5 p-3" style={{ backgroundColor: "rgba(201,168,76,0.06)" }}>
              <p className="text-[9px] tracking-[0.2em] uppercase mb-1" style={{ color: GOLD }}>Revenue MTD</p>
              <p className="font-display text-xl font-bold text-white">$42,800</p>
              <p className="text-[10px] text-emerald-400 mt-1">▲ 18%</p>
            </div>
            <div className="rounded-lg border p-3" style={{ borderColor: "rgba(95,168,168,0.3)", backgroundColor: "rgba(95,168,168,0.06)" }}>
              <p className="text-[9px] tracking-[0.2em] uppercase mb-1" style={{ color: TEAL }}>E.H.R.</p>
              <p className="font-display text-xl font-bold text-white">$214<span className="text-xs text-white/50">/hr</span></p>
              <p className="text-[10px] text-white/50 mt-1">Target $250</p>
            </div>
          </div>
          <div className="rounded-lg border border-white/5 p-4 bg-black/30">
            <div className="flex items-end gap-2 h-20">
              {[40, 55, 35, 60, 48, 75, 90].map((h, i) => (
                <div key={i} className="flex-1 rounded-sm" style={{ height: `${h}%`, backgroundColor: i >= 5 ? (i === 6 ? TEAL : GOLD) : "rgba(255,255,255,0.08)" }} />
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between text-[11px]"><span className="text-white/60">Proposal · Brand film</span><span style={{ color: GOLD }}>$18k</span></div>
            <div className="flex justify-between text-[11px]"><span className="text-white/60">Follow-up · Studio retainer</span><span className="text-white/80">$24k</span></div>
            <div className="flex justify-between text-[11px]"><span className="text-white/60">Proposal · Editorial shoot</span><span className="text-white/80">$16.5k</span></div>
          </div>
        </div>
      </div>
      <div className="absolute -bottom-6 -left-6 rounded-xl border border-white/10 px-4 py-3 shadow-xl backdrop-blur-xl" style={{ backgroundColor: "rgba(201,168,76,0.15)" }}>
        <div className="h-0.5 w-6 mb-2" style={{ backgroundColor: GOLD }} />
        <p className="text-[9px] tracking-[0.2em] uppercase text-white/70">Monthly Revenue</p>
        <p className="font-display text-lg font-bold text-white">+42%</p>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src={heroImage} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F0F]/80 via-[#0F0F0F]/90 to-[#0F0F0F]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_30%,rgba(201,168,76,0.15),transparent_60%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-24 lg:pt-36 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[10px] uppercase tracking-[0.25em] mb-8" style={{ borderColor: "rgba(201,168,76,0.4)", color: GOLD }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: GOLD }} />
              The CEO Dashboard For Creatives
            </span>
            <h1 className="font-display font-black uppercase leading-[0.92] tracking-tight text-5xl sm:text-6xl lg:text-[5.5rem] xl:text-[6.5rem] mb-8">
              <span className="block text-white">Lead Like</span>
              <span className="block text-white">A Creative</span>
              <span
                className="block"
                style={{
                  backgroundImage: `linear-gradient(90deg, ${GOLD}, ${TEAL})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                CEO.
              </span>
            </h1>
            <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase leading-tight tracking-tight mb-4">
              One Dashboard. Every Number That Matters.
            </p>
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed max-w-xl mb-4">
              See your <span className="text-white font-medium italic">revenue</span>, work your <span className="text-white font-medium italic">pipeline</span>, and track the <span className="text-white font-medium italic">daily habits</span> — let AI keep the rhythm, and build a wealthy creative business.
            </p>
            <p className="text-base sm:text-lg text-slate-400 max-w-xl mb-8">
              Log in each morning. Know exactly where you stand — and what to do next.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/auth"
                className="group inline-flex items-center gap-3 font-display font-bold px-7 py-4 text-sm uppercase tracking-[0.2em] transition-all hover:-translate-y-0.5 shadow-2xl"
                style={{ backgroundColor: GOLD, color: "#0F0F0F" }}
              >
                Start Your Free Trial
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/auth" className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors">
                Sign In →
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <DashboardMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

function Problem() {
  return (
    <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24 md:py-28 text-center">
      <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[1.1] tracking-normal text-foreground text-balance">
        You didn't pick up a camera, open Ableton, or design your first brand to spend your nights{" "}
        <span className="italic text-muted-foreground">chasing invoices</span> and{" "}
        <span className="italic text-muted-foreground">guessing at numbers</span>.
      </h2>
      <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto font-light leading-relaxed">
        Every untracked hour, every forgotten follow-up, every blurry month — <span className="text-teal font-medium">that's the real cost</span> of running your studio without a boardroom of your own.
      </p>
    </section>
  );
}

function Guide() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-28 grid lg:grid-cols-12 gap-16 items-center">
      <div className="lg:col-span-5 relative">
        <div className="absolute -inset-3 border border-gold/25" />
        <img src={heroImage} alt="A creative entrepreneur at work" className="relative w-full aspect-[4/3] object-cover saturate-50 hover:saturate-100 transition-all duration-1000" />
      </div>
      <div className="lg:col-span-7">
        <span className="block font-display text-xs font-bold uppercase tracking-normal mb-6 text-teal">
          The Guide
        </span>
        <h2 className="font-display text-3xl md:text-5xl font-bold leading-[1.05] tracking-normal mb-8">
          We've sat in your chair.
        </h2>
        <p className="text-lg text-foreground/80 leading-relaxed mb-6">
          The Wealthy Creatives Boardroom was built by creative entrepreneurs for creative entrepreneurs — people who've shipped the work, signed the deals, and felt the weight of doing it all alone.
        </p>
        <p className="text-lg text-foreground/80 leading-relaxed">
          This isn't another generic CRM bolted onto a Notion template. It's a CEO dashboard that respects your craft, sharpens your numbers, and gives you a quiet command room to make decisions like the leader you already are.
        </p>
      </div>
    </section>
  );
}

const VALUES = [
  { kicker: "01 // CLARITY", color: "text-teal", title: "Revenue Pulse", body: "One trusted number for money earned this month — with trend, source breakdown, and the yearly picture. Pull it in from Stripe or CSV, or type it yourself." },
  { kicker: "02 // GROWTH", color: "text-gold", title: "Deal Tracker", body: "A simple pipeline for every lead, offer, and closed-won deal — with notes, next actions, and shareable proposal pages. Export to CSV anytime." },
  { kicker: "03 // RHYTHM", color: "text-teal", title: "Daily Non-Negotiables", body: "The daily CEO habits that build wealthy studios — tracked with streaks and momentum so the small things compound." },
  { kicker: "04 // DIRECTION", color: "text-gold", title: "Vision & Goals", body: "Your 1, 3, 5, and 10-year vision anchored to dated milestones — and a living log of wins that proves you're building something real." },
  { kicker: "05 // CONTENT", color: "text-teal", title: "Content Tracker", body: "Track what you're making — from idea to published — and see which content actually turns into paid work." },
  { kicker: "06 // INTELLIGENCE", color: "text-gold", title: "AI Copilot", body: "Daily nudges, transcript summaries, and a Monday scorecard email that keep you on the path without the busywork." },
];

function ValueStack() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-28">
      <div className="grid md:grid-cols-3 gap-px bg-border border border-border rounded-lg overflow-hidden">
        {VALUES.map((v) => (
          <div key={v.title} className="group bg-background p-8 lg:p-10 hover:bg-surface-1 transition-colors duration-500">
            <div className={`text-xs font-bold mb-8 tracking-normal ${v.color}`}>{v.kicker}</div>
            <h3 className={`font-display text-2xl font-bold mb-4 transition-colors ${v.color === "text-teal" ? "group-hover:text-teal" : "group-hover:text-gold"}`}>{v.title}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">{v.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const PLAN = [
  { n: "01", color: "text-teal", title: "Set Your Anchor", body: "Name your Identity Anchor, your annual revenue goal, and the vision that will guide every decision this year." },
  { n: "02", color: "text-gold", title: "Run Your Rhythm", body: "Log wins, work your pipeline, and stack daily non-negotiables that protect both your output and your nervous system — nudged by AI, so the rhythm holds even on the hard weeks." },
  { n: "03", color: "text-teal", title: "Lead Like a CEO", body: "Use your dashboard and milestones to make sharper calls and grow your studio with intention." },
];

function Plan() {
  return (
    <section className="relative py-24 md:py-28 overflow-hidden">
      <div className="absolute -left-12 top-8 opacity-[0.04] select-none pointer-events-none">
        <h2 className="font-display text-[8rem] md:text-[14rem] font-black leading-none tracking-normaler">PLAN</h2>
      </div>
      <div className="relative max-w-5xl mx-auto px-6 lg:px-12">
        <h2 className="font-display text-xs font-bold mb-20 text-center uppercase tracking-normal">
          The Architecture of a Wealthy Studio
        </h2>
        <div className="space-y-12">
          {PLAN.map((step) => (
            <div key={step.n} className="group flex flex-col md:flex-row gap-6 md:gap-10 items-start">
              <div className={`font-display text-5xl font-light leading-none opacity-50 group-hover:opacity-100 transition-opacity ${step.color}`}>{step.n}</div>
              <div className="flex-1 border-b border-border pb-12">
                <h4 className="text-xl font-bold mb-3 uppercase tracking-normal">{step.title}</h4>
                <p className="text-muted-foreground leading-relaxed">{step.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PILLARS = [
  {
    color: "text-gold",
    kicker: "01 · Business",
    title: "Your money and your direction",
    body: "Revenue, expenses, profit and the vision behind it — one number for the month and one goal for the year. Connect Stripe or import a CSV and let the numbers flow in.",
  },
  {
    color: "text-teal",
    kicker: "02 · Marketing",
    title: "Where leads come from",
    body: "Capture leads, track your outreach and content — then see which source actually turns into paid work.",
  },
  {
    color: "text-gold",
    kicker: "03 · Sales",
    title: "Nothing slips",
    body: "Every lead and offer in one tracker with a next action and a date — plus shareable proposal pages your prospects can accept.",
  },
  {
    color: "text-teal",
    kicker: "04 · Delivery",
    title: "The work and what's blocking it",
    body: "Tasks, delegations, and a Vault for transcripts and SOPs that turn into action on their own.",
  },
];

function FourPillars() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-28">
      <div className="text-center mb-16">
        <span className="block font-display text-xs font-bold uppercase tracking-normal mb-6 text-teal">
          One Dashboard
        </span>
        <h2 className="font-display text-3xl md:text-5xl font-bold leading-[1.05] tracking-normal">
          Four parts of your business, on one screen.
        </h2>
        <p className="text-muted-foreground mt-6 max-w-2xl mx-auto leading-relaxed">
          Your money. Your next client. Your next deadline. The whole picture, without another spreadsheet to babysit.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-x-16 gap-y-10">
        {PILLARS.map((p) => (
          <div
            key={p.kicker}
            className="py-8 border-t border-border"
          >
            <p className={`text-xs font-bold uppercase tracking-normal mb-6 ${p.color}`}>
              {p.kicker}
            </p>
            <h3 className="font-display text-2xl font-bold mb-4">{p.title}</h3>
            <p className="text-muted-foreground leading-relaxed">{p.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}


function QuietOperator() {
  const items = [
    { color: "text-gold", title: "Daily Nudge", body: "One push a day at the time you choose, with one clear next step." },
    { color: "text-teal", title: "Monday Scorecard", body: "The week's numbers in your inbox every Monday morning — revenue, pipeline, and wins." },
    { color: "text-gold", title: "Transcript Summaries", body: "Drop a call recap into the Vault and get the action items and tasks back." },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-28 border-y border-border">
      <div className="text-center mb-16">
        <span className="block font-display text-xs font-bold uppercase tracking-normal mb-6 text-gold">
          The Quiet Operator
        </span>
        <h2 className="font-display text-3xl md:text-5xl font-bold leading-[1.05] tracking-normal">
          Your next move. Not more busywork.
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto mt-6 leading-relaxed">Advice grounded in your business numbers. Find the cash opportunity, work the right follow-up, and grow without running yourself into the ground.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {items.map((it) => (
          <div key={it.title} className="py-6 border-t border-border">
            <h3 className={`font-display text-xl font-bold mb-4 ${it.color}`}>{it.title}</h3>
            <p className="text-muted-foreground leading-relaxed">{it.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Stakes() {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-28 grid lg:grid-cols-2">
      <div className="bg-background p-12 lg:p-16 border-t border-border">
        <h3 className="font-display text-sm font-bold uppercase tracking-[0.25em] mb-10" >
          Without The Boardroom
        </h3>
        <ul className="space-y-6 text-muted-foreground font-light italic text-lg leading-relaxed">
          <li>Midnight invoice anxiety.</li>
          <li>Scope creep that quietly kills your margins.</li>
          <li>"Busy" work that feels like movement but is just spin.</li>
          <li>The nagging sense your talent deserves a better operator.</li>
        </ul>
      </div>
      <div className="bg-surface-1 p-12 lg:p-16 border-t" >
        <h3 className="font-display text-sm font-bold uppercase tracking-[0.25em] mb-10" >
          With The Boardroom
        </h3>
        <ul className="space-y-6 text-foreground font-medium text-lg leading-relaxed">
          <li>Clear 12-month revenue visibility.</li>
          <li>Shareable proposals with a clear next step.</li>
          <li>Predictable rhythms that leave space for actual art.</li>
          <li>The confidence of a CEO. The soul of a creative.</li>
        </ul>
      </div>
    </section>
  );
}

function Payoff() {
  return (
    <section className="relative text-center py-24 md:py-32 px-6 border-y border-teal/30">
      <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold mb-12 italic tracking-normal">
        Imagine <span className="text-gold">Monday</span> morning.
      </h2>
      <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-16 leading-relaxed">
        Open Boardroom. See where the money stands. Pick the follow-up that matters. Then get back to making the work you love.
      </p>
      <div className="mb-8 flex flex-col items-center gap-3">
        <DemoEntry className="h-12 px-6 border-gold text-gold" />
        <p className="text-sm text-muted-foreground">Sample business. No account creation needed.</p>
      </div>
      <Button asChild variant="link" className="text-gold">
        <Link to="/auth">Claim Your Seat <ArrowRight /></Link>
      </Button>
    </section>
  );
}

function Footer() {
  return (
    <footer className="max-w-7xl mx-auto px-6 lg:px-12 py-16 flex flex-col md:flex-row gap-6 items-center justify-between">
      <img src={logoHorizontal.url} alt="The Wealthy Creatives Boardroom" className="h-6 w-auto opacity-70" />
      <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
        © {new Date().getFullYear()} Wealthy Creatives. All rights reserved.
      </p>
    </footer>
  );
}

function LandingPage() {
  const { session, loading } = useAuth();
  useEffect(() => {
    if (!loading && session) window.location.replace("/dashboard");
  }, [session, loading]);
  return (

    <div className="min-h-screen bg-[#0F0F0F] text-[#F8F8F8] selection:bg-[#C9A84C] selection:text-[#0F0F0F] overflow-x-hidden">
      <Nav />
      <Hero />
      <div className="bg-background text-foreground [&_.font-display]:tracking-normal">
      <Problem />
      <Guide />
      <ValueStack />
      <FourPillars />
      <QuietOperator />
      <Plan />
      <Stakes />
      <Payoff />
      <Footer />
      </div>
    </div>
  );
}
